import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useDimensions from 'Util/useDimensions';
import icX1 from 'Assets/img/field/ic_x_666.png';
import icX2 from 'Assets/img/field/ic_x_666@2x.png';
import icX3 from 'Assets/img/field/ic_x_666@3x.png';
import icXCircle1 from 'Assets/img/field/ic_x_circle_b2_20.png';
import icXCircle2 from 'Assets/img/field/ic_x_circle_b2_20@2x.png';
import icXCircle3 from 'Assets/img/field/ic_x_circle_b2_20@3x.png';
import icSync1 from 'Assets/img/field/ic_sync_fff_18.png';
import icSync2 from 'Assets/img/field/ic_sync_fff_18@2x.png';
import icSync3 from 'Assets/img/field/ic_sync_fff_18@3x.png';
import icArrowRight1 from 'Assets/img/field/ic_arrow_666.png';
import icArrowRight2 from 'Assets/img/field/ic_arrow_666@2x.png';
import icArrowRight3 from 'Assets/img/field/ic_arrow_666@3x.png';
import icEyeOn from 'Assets/img/ic_eye_fill_24_basic@2x.png';
import icEyeOff from 'Assets/img/ic_eye_off_fill_24_basic@2x.png';
import { getImageSet } from 'Util/DataUtils';
import IntlMessages from 'Util/IntlMessages';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { removeEmpty } from 'Util/DataUtils';
import { useError } from 'Api/hooks/useError';
import { setLoading } from 'Actions/LoadingActions';
import { getStaffAllList, updateFieldStaffsWithRole } from 'Api/endpoints/User';
import UpdateStaffTable from './UpdateStaffTable';
import BasicPagination2 from 'Components/BasicPagination/BasicPagination2';
import StaffRole from 'Constants/StaffRole';
import Api from 'Api';
import { setAllFieldList, setDefaultField, updateMe } from 'Actions';
import { useNavigate } from 'react-router-dom';
import BasicSegmentPillBar from 'Components/common/BasicSegmentPillBar';
import BasicWarningDialog from 'Components/Dialog/BasicWarningDialog';

const icX = getImageSet([icX1, icX2, icX3]);
const icXCircle = getImageSet([icXCircle1, icXCircle2, icXCircle3]);
const icSync = getImageSet([icSync1, icSync2, icSync3]);
const icArrowRight = getImageSet([icArrowRight1, icArrowRight2, icArrowRight3]);

const PAGE_SIZE = 20;

const IS_ONLY_SELECT = [
  {
    value: 'all',
    labelId: 'staff.update.popup.header.all',
  },
  {
    value: 'select',
    labelId: 'staff.update.popup.header.select',
  },
];

const UpdatePopup = ({ isOpen, onClose, staffs, fieldId, refresh, deleteCallback }) => {
  const [originStaffs, setOriginStaffs] = useState(staffs);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { windowWidth } = useDimensions();
  const { formatMessage } = useIntl();
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [count, setCount] = useState(0);
  const [selectedStaffs, setSelectedStaffs] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [sort, setSort] = useState('');
  const [items, setItems] = useState([]);
  const [nowFilter, setNowFilter] = useState(null);
  const { showErrorPopup } = useError();
  const { user } = useSelector(state => state.authUser);
  const [isOnlySelect, setIsOnlySelect] = useState(false);
  const [isShowRestricted, setIsShowRestricted] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);

  const isSupervisor = useMemo(() => {
    if (!user.isStaff) return true;
    const staff = staffs.find(x => x._id === user.partner.materialBuyer.staffs._id);
    return staff?.role === StaffRole.SUPERVISOR;
  }, [user, staffs]);

  const _setSort = useCallback(sort => {
    setSort(sort);
    setPage(1);
  }, []);

  const getOriginStaffs = useCallback(async () => {
    const data = removeEmpty({
      sort: 'id',
    });

    try {
      const result = await getStaffAllList(data);
      if (result.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }
      setOriginStaffs(result.data.items);
    } catch (e) {
      console.log(e);
    }
  }, [showErrorPopup]);

  const getList = useCallback(async () => {
    let filteredStaffs = [...originStaffs];

    // 필터링 로직
    if (nowFilter) {
      filteredStaffs = filteredStaffs.filter(staff => {
        const nameMatch = !nowFilter.name || staff.name.toLowerCase().includes(nowFilter.name.toLowerCase());
        const phoneMatch = !nowFilter.phone || staff.phone.includes(nowFilter.phone);
        return nameMatch && phoneMatch;
      });
    }

    if (isOnlySelect) {
      filteredStaffs = filteredStaffs.filter(staff => selectedStaffs.some(selected => selected._id === staff._id));
    }

    if (!isShowRestricted) {
      filteredStaffs = filteredStaffs.filter(staff => staff.active);
    }

    // 정렬 로직
    filteredStaffs.sort((a, b) => {
      if (sort === '' || sort === 'id') {
        return a.id - b.id;
      } else if (sort === '-id') {
        return b.id - a.id;
      } else if (sort === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sort === '-name') {
        return b.name.localeCompare(a.name);
      } else if (sort === 'role') {
        // selectedStaffs에서 role 정보를 찾아서 사용
        const aSelected = selectedStaffs.find(s => s._id === a._id);
        const bSelected = selectedStaffs.find(s => s._id === b._id);
        const aRole = aSelected?.role || a.role || 'MEMBER';
        const bRole = bSelected?.role || b.role || 'MEMBER';
        return aRole.localeCompare(bRole);
      } else if (sort === '-role') {
        // selectedStaffs에서 role 정보를 찾아서 사용
        const aSelected = selectedStaffs.find(s => s._id === a._id);
        const bSelected = selectedStaffs.find(s => s._id === b._id);
        const aRole = aSelected?.role || a.role || 'MEMBER';
        const bRole = bSelected?.role || b.role || 'MEMBER';
        return bRole.localeCompare(aRole);
      } else if (sort === 'department') {
        const aName = a.department?.name || '';
        const bName = b.department?.name || '';
        return aName.localeCompare(bName);
      } else if (sort === '-department') {
        const aName = a.department?.name || '';
        const bName = b.department?.name || '';
        return bName.localeCompare(aName);
      } else if (sort === 'dateEntered') {
        return new Date(a.dateEntered) - new Date(b.dateEntered);
      } else if (sort === '-dateEntered') {
        return new Date(b.dateEntered) - new Date(a.dateEntered);
      } else if (sort === 'phone') {
        return a.phone.localeCompare(b.phone);
      } else if (sort === '-phone') {
        return b.phone.localeCompare(a.phone);
      } else if (sort === 'birth') {
        return new Date(a.birth) - new Date(b.birth);
      } else if (sort === '-birth') {
        return new Date(b.birth) - new Date(a.birth);
      }
      return 0;
    });

    // 카운트 업데이트
    setCount(filteredStaffs.length);
    setLastPage(Math.ceil(filteredStaffs.length / PAGE_SIZE));

    // 페이지네이션 적용
    const startIndex = (page - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const paginatedStaffs = filteredStaffs.slice(startIndex, endIndex);

    setItems(paginatedStaffs);
  }, [nowFilter, page, sort, originStaffs, isOnlySelect, selectedStaffs, isShowRestricted]);

  useEffect(() => {
    getOriginStaffs();
  }, [getOriginStaffs]);

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setLastPage(0);
    setCount(0);
    // props의 staffs에 기존 role 정보를 포함하여 selectedStaffs 설정
    setSelectedStaffs(
      staffs.map(staff => ({
        _id: staff._id,
        name: staff.name,
        role: staff.role || 'MEMBER', // 기존 role이 있으면 사용, 없으면 기본값
      }))
    );
    setName('');
    setPhone('');
    setSort('');
    setItems([]);
    setNowFilter(null);
  }, [isOpen, staffs]);

  useEffect(() => {
    if (!isOpen || originStaffs.length === 0) return;
    getList();
  }, [isOpen, getList, originStaffs.length]);

  useEffect(() => {
    if (!isOpen || originStaffs.length === 0) return;
    getList();
  }, [isOpen, originStaffs.length, staffs, getList]);

  const toggleCheck = useCallback(
    item => {
      setSelectedStaffs(prev => {
        if (prev.some(x => x._id === item._id)) {
          return prev.filter(x => x._id !== item._id);
        }
        // isSupervisor가 아닌 경우 MEMBER 권한으로 고정
        const role = isSupervisor ? item.role || StaffRole.MEMBER : StaffRole.MEMBER;
        return [...prev, { _id: item._id, name: item.name, role }];
      });
    },
    [isSupervisor]
  );

  // 전체 선택/해제 핸들러
  const handleSelectAll = useCallback(
    (checked, selectableItems = null) => {
      if (checked) {
        // selectableItems가 전달되면 해당 아이템들만 선택, 그렇지 않으면 모든 아이템 선택
        const targetItems = selectableItems || items;
        // isSupervisor가 아닌 경우 MEMBER 권한으로 고정
        const newStaffs = targetItems.map(item => ({
          _id: item._id,
          name: item.name,
          role: isSupervisor ? item.role || StaffRole.MEMBER : StaffRole.MEMBER,
        }));

        if (!isSupervisor) {
          // isSupervisor가 아닌 경우 기존 staffs는 유지하고 새로운 staffs만 추가
          setSelectedStaffs(prev => {
            const existingStaffs = prev.filter(staff => staffs.some(original => original._id === staff._id));
            return [...existingStaffs, ...newStaffs];
          });
        } else {
          // isSupervisor인 경우에도 기존 선택을 유지하고 새로운 아이템들만 추가
          setSelectedStaffs(prev => {
            const existingStaffs = prev.filter(staff => !targetItems.some(item => item._id === staff._id));
            return [...existingStaffs, ...newStaffs];
          });
        }
      } else {
        if (!isSupervisor) {
          // isSupervisor가 아닌 경우 기존 staffs는 유지하고 새로 추가된 것만 제거
          setSelectedStaffs(prev => prev.filter(staff => staffs.some(original => original._id === staff._id)));
        } else {
          // isSupervisor인 경우에도 현재 페이지의 아이템들만 해제
          const targetItems = selectableItems || items;
          setSelectedStaffs(prev => prev.filter(staff => !targetItems.some(item => item._id === staff._id)));
        }
      }
    },
    [items, isSupervisor, staffs]
  );

  // 전체 선택 상태 확인
  const isAllSelected = useMemo(() => {
    if (items.length === 0) return false;

    if (!isSupervisor) {
      // isSupervisor가 아닌 경우 선택 가능한 아이템들만으로 계산
      const selectableItems = items.filter(item => !staffs.some(original => original._id === item._id));
      if (selectableItems.length === 0) return false;

      // 선택 가능한 아이템들이 모두 선택되었는지 확인
      const selectedSelectableItems = selectedStaffs.filter(staff => selectableItems.some(item => item._id === staff._id));
      return selectedSelectableItems.length === selectableItems.length;
    } else {
      // isSupervisor인 경우 현재 페이지의 모든 아이템이 선택되었는지 확인
      const selectedItems = selectedStaffs.filter(staff => items.some(item => item._id === staff._id));
      return selectedItems.length === items.length && items.length > 0;
    }
  }, [items, selectedStaffs, isSupervisor, staffs]);

  // 권한 변경 핸들러
  const handleRoleChange = useCallback((staffId, role) => {
    setSelectedStaffs(prev => prev.map(staff => (staff._id === staffId ? { ...staff, role } : staff)));
  }, []);

  const setInitFieldListsConfig = useCallback(async () => {
    const fieldList = [];
    const result = await Api.user.getFieldAllList({ page: 1, active: true });
    if (result.data) {
      fieldList.push(...result.data.items);
    }
    dispatch(setAllFieldList(fieldList));
  }, [dispatch]);

  const onUpdateStaff = async () => {
    const currentUserId = user.partner?.materialBuyer?.staffs?._id;
    const isCurrentUserIncluded = selectedStaffs.some(staff => staff._id === currentUserId);
    if (user.isStaff && !isCurrentUserIncluded) {
      setOpenWarningDialog(true);
      return;
    }

    const data = {
      fieldId,
      staffs: selectedStaffs.map(x => ({
        staff: x._id,
        role: x.role || 'MEMBER',
      })),
    };

    try {
      dispatch(setLoading(true));
      const result = await updateFieldStaffsWithRole(data);
      if (result.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }
      refresh(result.data);
      onClose();
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onConfirmLeave = async () => {
    const data = {
      fieldId,
      staffs: selectedStaffs.map(x => ({
        staff: x._id,
        role: x.role || 'MEMBER',
      })),
    };

    try {
      dispatch(setLoading(true));
      const result = await updateFieldStaffsWithRole(data);
      if (result.err) {
        showErrorPopup({ text: result.err.response.data.message });
        return;
      }

      const resultMe = await Api.auth.me();
      if (resultMe.data) {
        dispatch(updateMe(resultMe.data));
        await setInitFieldListsConfig();
        dispatch(setDefaultField(resultMe.data?.partner?.materialBuyer?.defaultField));
        navigate('/admin/dashboard/field', { replace: true });
      }
      onClose();
      setOpenWarningDialog(false);
      deleteCallback && deleteCallback();
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      <Dialog maxWidth={windowWidth < 1919 ? 'lg' : 'xl'} fullWidth open={isOpen} onClose={null} PaperProps={{ style: { borderRadius: 15 } }}>
        <DialogTitle className='field-setting-staff-update-popup-head'>
          <div className='field-setting-staff-update-popup-head-title'>
            <IntlMessages id='field.staff.update.title' />
          </div>
          <button className='field-setting-staff-update-popup-head-close no-drag cursor-pointer' onClick={onClose}>
            <img {...icX} alt='close' />
          </button>
        </DialogTitle>
        <DialogContent style={{ padding: 0 }}>
          <div className='field-setting-staff-update-popup-body'>
            <div className='field-setting-staff-update-popup-filter'>
              <div className='field-setting-staff-update-popup-filter-item'>
                <div className='field-setting-staff-update-popup-filter-item-title'>
                  <IntlMessages id='user.name' />
                </div>
                <div className='field-setting-staff-update-popup-filter-item-input'>
                  <input
                    type='text'
                    placeholder={formatMessage({ id: 'field.staff.update.popup.filter.name' })}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setNowFilter({ name, phone });
                        setPage(1);
                      }
                    }}
                  />
                  {name && <img {...icXCircle} alt='close' className='cursor-pointer no-drag' onClick={() => setName('')} />}
                </div>
              </div>
              <div className='field-setting-staff-update-popup-filter-item'>
                <div className='field-setting-staff-update-popup-filter-item-title'>
                  <IntlMessages id='common.phone' />
                </div>
                <div className='field-setting-staff-update-popup-filter-item-input'>
                  <input
                    type='text'
                    placeholder={formatMessage({ id: 'field.staff.update.popup.filter.phone' })}
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        setNowFilter({ name, phone });
                        setPage(1);
                      }
                    }}
                  />
                  {phone && <img {...icXCircle} alt='close' className='cursor-pointer no-drag' onClick={() => setPhone('')} />}
                </div>
              </div>
              <button
                className='field-setting-staff-update-popup-filter-item-button-search cursor-pointer no-drag'
                onClick={() => {
                  setNowFilter({ name, phone });
                  setPage(1);
                }}
              >
                <IntlMessages id='button.search' />
              </button>
              <button
                className={`field-setting-staff-update-popup-filter-item-button-reset no-drag ${nowFilter ? 'cursor-pointer' : 'disabled'}`}
                onClick={() => {
                  setNowFilter(null);
                  setName('');
                  setPhone('');
                  setPage(1);
                }}
                disabled={!nowFilter}
              >
                <img {...icSync} alt='sync' />
                <IntlMessages id='widgets.reset' />
              </button>
            </div>
            <div className='field-setting-staff-update-popup-header'>
              <div className='field-setting-staff-update-popup-header-left'>
                <div className='field-setting-staff-update-popup-header-title'>
                  <div className='field-setting-staff-update-popup-header-title-text'>
                    {nowFilter ? <IntlMessages id='dashboard.field.account.book.search.result' /> : <IntlMessages id='field.staff.update.popup.header.title' />}
                  </div>
                  <div className='field-setting-staff-update-popup-header-title-count'>{count}</div>
                </div>
                <BasicSegmentPillBar value={isOnlySelect ? 'select' : 'all'} onChange={e => setIsOnlySelect(e.target.value === 'select')} menuItems={IS_ONLY_SELECT} />
                <div className='field-setting-staff-update-popup-header-restricted cursor-pointer no-drag' onClick={() => setIsShowRestricted(!isShowRestricted)}>
                  <img src={isShowRestricted ? icEyeOff : icEyeOn} alt='eye' style={{ width: '24px', height: '24px' }} />
                  <div>
                    <IntlMessages id='staff.update.popup.header.restricted' />
                  </div>
                </div>
              </div>
              {!user.isStaff && (
                <button className='field-setting-staff-update-popup-header-button cursor-pointer no-drag' onClick={() => window.open('/admin/staff/list', '_blank')}>
                  <IntlMessages id='field.staff.add' />
                  <div className='field-setting-staff-update-popup-header-button-arrow'>
                    <img {...icArrowRight} alt='arrow-right' />
                  </div>
                </button>
              )}
            </div>
            <UpdateStaffTable
              windowWidth={windowWidth}
              items={items}
              selectedStaffs={selectedStaffs}
              toggleCheck={toggleCheck}
              setSort={_setSort}
              sort={sort}
              nowFilter={nowFilter}
              isAllSelected={isAllSelected}
              onSelectAll={handleSelectAll}
              onRoleChange={handleRoleChange}
              isSupervisor={isSupervisor}
              originalStaffs={staffs}
            />
          </div>
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', width: '100%' }}>
            <BasicPagination2 currentPage={page} lastPage={lastPage} onPageClick={(e, page) => setPage(page)} />
          </div>
          <div className='field-setting-staff-update-popup-button'>
            <button className='field-setting-staff-update-popup-button-close cursor-pointer no-drag' onClick={onClose}>
              <IntlMessages id='button.close' />
            </button>
            <button className='field-setting-staff-update-popup-button-save cursor-pointer no-drag' onClick={onUpdateStaff}>
              <IntlMessages id='field.staff.update.popup.button.save' values={{ count: selectedStaffs.length }} />
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <BasicWarningDialog
        open={openWarningDialog}
        onClose={() => setOpenWarningDialog(false)}
        onConfirm={onConfirmLeave}
        titleId='staff.update.popup.header.leave.title'
        confirmId='staff.update.popup.header.leave.confirm'
      />
    </>
  );
};

export default UpdatePopup;
